<template>
  <div class="n-input checkbox" :class="{ 'has-error': errorMessage }">
    <div class="checkbox__input-container">
      <input
        type="checkbox"
        :name="name"
        :id="inputId"
        :checked="checked"
        :disabled="disabled"
        @change="handleCheckboxChange"
      />
      <label class="checkbox__label" :class="{ 'checkbox__label--disabled': disabled }" :for="inputId">
        {{ label }}
      </label>
    </div>
    <span v-if="errorMessage" class="error-message">{{ errorMessage }}</span>
  </div>
</template>

<script lang="ts" setup>
import { useIdGenerator } from '@/shared/designsystem/useIdGenerator';
import { useVModel } from '@vueuse/core';

defineOptions({
  name: 'n-checkbox',
});

const props = defineProps<{
  id?: string;
  label: string;
  name: string;
  errorMessage?: string;
  checked: boolean;
  disabled: boolean;
}>();

// Define emits
const emit = defineEmits<{
  (e: 'update:checked', value: boolean): void;
  (e: 'change', value: boolean): void;
}>();

const value = useVModel(props, 'checked', emit);

// Use the IdGenerator composable
const { inputId } = useIdGenerator(props.id);

// Handle checkbox change
function handleCheckboxChange(event: Event) {
  const target = event.currentTarget as HTMLInputElement;
  value.value = target.checked;
  emit('change', target.checked);
}
</script>

<style lang="scss" scoped>
@use '../../../styles/colours' as *;
@use '../../../styles/input' as *;
.checkbox {
  border: none;
  padding: 0;
  &__input-container {
    display: flex;
  }
  input[type='checkbox'] {
    display: inline-block;
    padding: 0;
    border: none;
    box-shadow: none;
    width: auto;
    margin-right: 0.5rem;
  }
  label.checkbox__label {
    display: flex;
    margin: 0;
    cursor: pointer;
    justify-content: flex-start;
  }
  .error-message {
    padding-left: 0.25rem;
  }
}
</style>
